// src/components/Utils/HTMLEditor.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, ChevronLeft,  Download, Copy, Check, Eye, EyeOff, Play, RotateCcw, Maximize2, Minimize2, Volume2, VolumeX, X } from 'lucide-react';
import './HTMLEditor.css';
import './common_Utils.css';
import ttsManager from './TTS';

const HTMLEditor = ({ onBack, user, userProfile, initialFileContent }) => { // [수정] initialFileContent prop 추가
  const defaultContent = (`<!DOCTYPE html>
<html lang="ko">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>HTML 편집기</title>
    <style>
        body {
            font-family: Arial, sans-serif;
            padding: 20px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            margin: 0;
        }
        .container {
            max-width: 800px;
            margin: 0 auto;
            background: white;
            padding: 40px;
            border-radius: 20px;
            color: #333;
            box-shadow: 0 10px 40px rgba(0,0,0,0.2);
        }
        h1 {
            color: #667eea;
            margin-bottom: 20px;
        }
        p {
            line-height: 1.6;
            margin: 10px 0;
        }
        .button {
            background: linear-gradient(135deg, #667eea, #764ba2);
            color: white;
            padding: 12px 24px;
            border: none;
            border-radius: 8px;
            cursor: pointer;
            font-size: 16px;
            margin-top: 20px;
        }
        .button:hover {
            transform: scale(1.05);
        }
    </style>
</head>
<body>
    <div class="container">
        <h1>HTML 편집기에 오신 것을 환영합니다!</h1>
        <p>이곳에서 HTML, CSS, JavaScript를 자유롭게 작성하고 실시간으로 결과를 확인할 수 있습니다.</p>
        <p>왼쪽 편집기에 코드를 입력하면 오른쪽에서 실시간으로 미리보기됩니다.</p>
        <button class="button" onclick="alert('버튼이 클릭되었습니다!')">클릭해보세요</button>
    </div>
    
    <script>
        console.log('HTML 편집기가 로드되었습니다!');
    </script>
</body>
</html>`);

  const [html, setHtml] = useState(initialFileContent || defaultContent);
  const [copied, setCopied] = useState(false);
  const [fullScreen, setFullScreen] = useState(true);
  const [isAutoRun, setIsAutoRun] = useState(true);
  const [isDragging, setIsDragging] = useState(false); // 🚨 [추가] 드래그 상태
  const [isEditorFullscreen, setIsEditorFullscreen] = useState(false);
  const [isPreviewFullscreen, setIsPreviewFullscreen] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const iframeRef = useRef(null);

  const [activeTab, setActiveTab] = useState('editor');

  // frontPane 상태 추가
  const [frontPane, setFrontPane] = useState('editor');
  
  // 드래그 이동 관련 상태
  const [editorPosition, setEditorPosition] = useState({ x: 0, y: 0 }); 
  const getInitialPreviewPosition = () => {
    // 에디터 컨테이너의 너비를 기준으로 50% 계산
    const containerWidth = window.innerWidth;
      return { x: containerWidth * 0.5, y: 0 };
    };
  
  const [previewPosition, setPreviewPosition] = useState(getInitialPreviewPosition());
  const [draggingPane, setDraggingPane] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  // 팝업 창 관련
  const [editorPopup, setEditorPopup] = useState(null);
  const [previewPopup, setPreviewPopup] = useState(null);

  // HTML 실행 (수동 실행 시 사용되나, srcDoc 사용으로 현재는 비활성)
  const runCode = () => {
    if (iframeRef.current) {
      const iframe = iframeRef.current;
      const document = iframe.contentDocument || iframe.contentWindow.document;
      document.open();
      document.write(html);
      document.close();
    }
  };

  // 자동 실행
  useEffect(() => {
    if (isAutoRun) {
      const timer = setTimeout(() => {
        // runCode(); // srcDoc 사용으로 주석 처리
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [html, isAutoRun, fullScreen]);

  const handleDownload = () => {
    const element = document.createElement('a');
    const file = new Blob([html], { type: 'text/html' });
    element.href = URL.createObjectURL(file);
    element.download = 'document.html';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(html);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('복사 실패:', err);
    }
  };

  // 🚨 [추가] 드롭된 파일을 읽고 setHtml을 수행하는 핵심 로직
  const readDroppedFile = (file) => {
    if (file) {
      const reader = new FileReader();
        reader.onload = (e) => {
          let content = e.target.result;
          
          // 줄 바꿈 문자 통일 로직
          content = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
          
          setHtml(content); // 통일된 줄 바꿈 문자를 가진 content를 설정
        };
        reader.readAsText(file);
    }
  };

  // [수정] 파일 업로드 로직을 readDroppedFile 재활용하도록 변경
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      readDroppedFile(file);
    }
  };

  // 🚨 [추가] 드래그 오버 이벤트 핸들러 (기본 동작 방지 및 시각적 피드백 시작)
  const handleDragOver = (event) => {
    event.preventDefault(); // 기본 동작(파일 열기) 방지
    setIsDragging(true);
  };

  // 🚨 [추가] 드래그 리브 이벤트 핸들러 (시각적 피드백 종료)
  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragging(false);
  };

  // 🚨 [추가] 드롭 이벤트 핸들러
  const handleDrop = (event) => {
    event.preventDefault(); // 기본 동작 방지
    setIsDragging(false); // 드래그 상태 종료

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      // HTML 파일 확장자만 허용하는 간단한 검증
      if (file.name.endsWith('.html') || file.name.endsWith('.htm')) {
        readDroppedFile(file);
      } else {
        alert('HTML 파일 (.html, .htm)만 드롭할 수 있습니다.');
      }
    }
  };

  const handleReset = () => {
    if (window.confirm('편집기를 초기화하시겠습니까?')) {
      setHtml(`<!DOCTYPE html>
<html lang="ko">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>새 문서</title>
    <style>
        body {
            font-family: Arial, sans-serif;
            padding: 20px;
        }
    </style>
</head>
<body>
    <h1>새 HTML 문서</h1>
    <p>여기에 내용을 작성하세요.</p>
</body>
</html>`);
    }
  };

  // 언어 감지 함수
  // Text-to-Speech 핸들러
  const handleTextToSpeech = () => {
    const success = ttsManager.speak(
      html,
      () => setIsSpeaking(true),  // onStart
      () => setIsSpeaking(false), // onEnd
      (error) => console.error('TTS 오류:', error) // onError
    );
    
    // 이미 실행 중이었다면 중지된 것이므로 상태 업데이트
    if (!success && isSpeaking) {
      setIsSpeaking(false);
    }
  };

  // 컴포넌트 언마운트 시 음성 중지
  useEffect(() => {
    return () => {
      ttsManager.stop();
    };
  }, []);

  // Pane 드래그 시작  
  const handlePaneDragStart = (paneType, e) => {
    if (e.target.closest('.pane-header')) {
      setDraggingPane(paneType);
      
      // 현재 패인의 위치 가져오기
      const pane = e.currentTarget.parentElement; // pane-header의 부모 요소(editor-pane 또는 preview-pane)
      const rect = pane.getBoundingClientRect();
      
      // 드래그 시작 시 다른 pane의 현재 위치도 고정
      if (paneType === 'editor') {
        // preview의 현재 위치 저장
        const previewPane = document.querySelector('.preview-pane');
        if (previewPane && (previewPosition.x === 0 && previewPosition.y === 0)) {
          const previewRect = previewPane.getBoundingClientRect();
          setPreviewPosition({ x: previewRect.left, y: previewRect.top });
        }
      } else if (paneType === 'preview') {
        // editor의 현재 위치 저장
        const editorPane = document.querySelector('.editor-pane');
        if (editorPane && (editorPosition.x === 0 && editorPosition.y === 0)) {
          const editorRect = editorPane.getBoundingClientRect();
          setEditorPosition({ x: editorRect.left, y: editorRect.top });
        }
      }
      
      // 마우스 클릭 위치와 패인의 현재 위치 차이 계산
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };

  // 마우스 이동 핸들러
  useEffect(() => {
    if (!draggingPane) return;

    const handleMouseMove = (e) => {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      if (draggingPane === 'editor') {
        setEditorPosition({ x: newX, y: newY });
      } else if (draggingPane === 'preview') {
        setPreviewPosition({ x: newX, y: newY });
      }
    };

    const handleMouseUp = () => {
      setDraggingPane(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingPane, dragOffset]);

  // Editor 팝업 창 열기
  const openEditorInNewWindow = (e) => {
    e.stopPropagation();
    
    if (editorPopup && !editorPopup.closed) {
      editorPopup.focus();
      return;
    }

    const width = 1280;
    const height = 768;
    // 현재 브라우저 창이 있는 화면 기준으로 중앙 계산
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open('', 'EditorPopup', `width=${width},height=${height},left=${left},top=${top}`);

    // const popup = window.open('', 'EditorPopup', 'width=800,height=600,left=100,top=100');
    if (!popup) {
      alert('팝업이 차단되었습니다. 팝업 차단을 해제해주세요.');
      return;
    }

    setEditorPopup(popup);

    popup.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>HTML Editor</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', system-ui, sans-serif;
            background: #1e1e1e;
            color: #d4d4d4;
            height: 100vh;
            display: flex;
            flex-direction: column;
          }
          .header {
            background: #2d2d30;
            padding: 12px 20px;
            border-bottom: 1px solid #3e3e42;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .header h2 {
            font-size: 16px;
            font-weight: 500;
            color: #cccccc;
          }
          .char-count {
            font-size: 12px;
            color: #858585;
          }
          textarea {
            flex: 1;
            width: 100%;
            padding: 20px;
            background: #1e1e1e;
            color: #d4d4d4;
            border: none;
            font-family: 'Consolas', 'Monaco', monospace;
            font-size: 14px;
            line-height: 1.6;
            resize: none;
            outline: none;
          }
          textarea::selection {
            background: #264f78;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h2>📝 HTML Editor</h2>
          <span class="char-count" id="charCount">${html.length} 글자</span>
        </div>
        <textarea id="editor" spellcheck="false">${html}</textarea>
        <script>
          const textarea = document.getElementById('editor');
          const charCount = document.getElementById('charCount');
          
          textarea.addEventListener('input', () => {
            charCount.textContent = textarea.value.length + ' 글자';
            if (window.opener && !window.opener.closed) {
              window.opener.postMessage({
                type: 'UPDATE_HTML',
                content: textarea.value
              }, '*');
            }
          });

          window.addEventListener('message', (event) => {
            if (event.data.type === 'UPDATE_HTML') {
              textarea.value = event.data.content;
              charCount.textContent = event.data.content.length + ' 글자';
            }
          });

          window.addEventListener('beforeunload', () => {
            if (window.opener && !window.opener.closed) {
              window.opener.postMessage({ type: 'POPUP_CLOSED', popup: 'editor' }, '*');
            }
          });
        </script>
      </body>
      </html>
    `);
    popup.document.close();
  };

  // Preview 팝업 창 열기
  const openPreviewInNewWindow = (e) => {
    e.stopPropagation();
    
    if (previewPopup && !previewPopup.closed) {
      previewPopup.focus();
      return;
    }

    const width = 1280;
    const height = 768;
    // 현재 브라우저 창이 있는 화면 기준으로 중앙 계산
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open('', 'PreviewPopup', `width=${width},height=${height},left=${left},top=${top}`); 
    if (!popup) {
      alert('팝업이 차단되었습니다. 팝업 차단을 해제해주세요.');
      return;
    }

    setPreviewPopup(popup);

    popup.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>HTML Preview</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', system-ui, sans-serif;
            background: #ffffff;
            height: 100vh;
            display: flex;
            flex-direction: column;
          }
          .header {
            background: #f3f3f3;
            padding: 12px 20px;
            border-bottom: 1px solid #e0e0e0;
          }
          .header h2 {
            font-size: 16px;
            font-weight: 500;
            color: #333;
          }
          iframe {
            flex: 1;
            width: 100%;
            border: none;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h2>👁️ HTML Preview</h2>
        </div>
        <iframe id="preview" sandbox="allow-scripts allow-same-origin allow-modals"></iframe>
        <script>
          const iframe = document.getElementById('preview');
          
          window.addEventListener('message', (event) => {
            if (event.data.type === 'UPDATE_HTML') {
              const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
              iframeDoc.open();
              iframeDoc.write(event.data.content);
              iframeDoc.close();
            }
          });

          window.addEventListener('beforeunload', () => {
            if (window.opener && !window.opener.closed) {
              window.opener.postMessage({ type: 'POPUP_CLOSED', popup: 'preview' }, '*');
            }
          });

          // 초기 렌더링
          if (window.opener && !window.opener.closed) {
            window.opener.postMessage({ type: 'REQUEST_HTML' }, '*');
          }
        </script>
      </body>
      </html>
    `);
    popup.document.close();
  };

  // 팝업과 동기화
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === 'UPDATE_HTML') {
        setHtml(event.data.content);
      } else if (event.data.type === 'POPUP_CLOSED') {
        if (event.data.popup === 'editor') {
          setEditorPopup(null);
        } else if (event.data.popup === 'preview') {
          setPreviewPopup(null);
        }
      } else if (event.data.type === 'REQUEST_HTML') {
        if (previewPopup && !previewPopup.closed) {
          previewPopup.postMessage({ type: 'UPDATE_HTML', content: html }, '*');
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [html, previewPopup]);

  useEffect(() => {
    if (editorPopup && !editorPopup.closed) {
      editorPopup.postMessage({ type: 'UPDATE_HTML', content: html }, '*');
    }
    if (previewPopup && !previewPopup.closed) {
      previewPopup.postMessage({ type: 'UPDATE_HTML', content: html }, '*');
    }
  }, [html, editorPopup, previewPopup]);

  // Drag and Drop 핸들러 수정
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragOverCallback = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragLeaveCallback = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  }, []);

  const handleDropCallback = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      readDroppedFile(files[0]);
    }
  }, []); 

  return (
    <div 
      className={`html-editor ${isDragging ? 'dragging' : ''}`}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOverCallback}
      onDragLeave={handleDragLeaveCallback}
      onDrop={handleDropCallback}
    >
      <div className="html-editor-header">
        <div 
          onClick={onBack}  
          className="common-back-button"
        >
          <ChevronLeft size={24} />
        </div>
        <h1>HTML 편집기</h1>
        <div className="header-actions">
          <label className="upload-btn">
            <input 
              type="file" 
              accept=".html,.htm" 
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            파일 열기
          </label>
          <button 
            onClick={() => setIsAutoRun(!isAutoRun)} 
            className={`auto-run-btn ${isAutoRun ? 'active' : ''}`}
          >
            {isAutoRun ? '자동 실행 ON' : '자동 실행 OFF'}
          </button>
          {!isAutoRun && (
            <button onClick={runCode} className="action-btn">
              <Play className="icon" />
              실행
            </button>
          )}
          <button onClick={() => setFullScreen(!fullScreen)} className="preview-toggle-btn">
            {fullScreen ? <EyeOff className="icon" /> : <Eye className="icon" />}
            {fullScreen ? 'Split View' : 'Full Screen'}
          </button>
          {/* <button onClick={handleReset} className="action-btn reset">
            <RotateCcw className="icon" />
            초기화
          </button>
          <button onClick={handleCopy} className="action-btn">
            {copied ? <Check className="icon" /> : <Copy className="icon" />}
            {copied ? '복사됨!' : '복사'}
          </button> */}
          <button onClick={handleDownload} className="action-btn">
            <Download className="icon" />
            다운로드
          </button>
        </div>
      </div>

      <div className="tab-buttons"> 
        <button 
          className={`tab-btn ${activeTab === 'editor' ? 'active' : ''}`}
          onClick={() => setActiveTab('editor')}
        >
          📝 Editor
        </button>
        <button 
          className={`tab-btn ${activeTab === 'preview' ? 'active' : ''}`}
          onClick={() => {setActiveTab('preview'); setPreviewPosition({ x: 0, y: 0 }); }}
        >
          Preview
        </button>
        <button 
          className={`tab-btn ${activeTab === 'both' ? 'active' : ''}`}
          onClick={() => setActiveTab('both')}
        >
          Both
        </button> 
      </div>

      <div className={`html-editor-content ${fullScreen ? 'split' : 'full'}`}>
        {(activeTab === 'editor' || activeTab === 'both') && (
        <div 
          className={`editor-pane ${isEditorFullscreen ? 'fullscreen' : ''} ${draggingPane === 'editor' ? 'dragging-pane' : ''}`}
          style={{
            position: editorPosition.x !== 0 || editorPosition.y !== 0 ? 'absolute' : 'static',
            left: editorPosition.x !== 0 ? `${editorPosition.x}px` : 'auto',
            top: editorPosition.y !== 0 ? `${editorPosition.y}px` : 'auto',
            width: editorPosition.x !== 0 || editorPosition.y !== 0 ? 'calc(50% - 10px)' : 'auto',
            height: editorPosition.x !== 0 || editorPosition.y !== 0 ? '600px' : 'auto',
            zIndex: (draggingPane === 'editor' || frontPane === 'editor') ? 1000 : 1
          }}
          onClick={() => setFrontPane('editor')}
        >
          <div 
            className="pane-header draggable-header"
            onMouseDown={(e) => handlePaneDragStart('editor', e)}
            style={{ cursor: 'move' }}
          >
            <h3>HTML 코드</h3>
            <div className="header-right">
              <span className="char-count">{html.length} 글자</span>
              <button 
                className="popup-btn" 
                onClick={openEditorInNewWindow}
                title="새 창으로 열기"
              >
                <Eye className="icon" />
              </button>
              <button 
                className="editor-copy-btn"
                onClick={handleCopy}
                title="편집기 내용 복사"
              >
                {copied ? <Check className="icon" /> : <Copy className="icon" />}
              </button>
              <button 
                className="editor-clear-btn" 
                onClick={() => setHtml('')}
                title="편집기 내용 지우기"
              >
                <X className="icon" />
              </button>
              <button 
                className="fullscreen-btn" 
                onClick={() => setIsEditorFullscreen(!isEditorFullscreen)}
                title={isEditorFullscreen ? "축소" : "전체화면"}
              >
                {isEditorFullscreen ? <Minimize2 className="icon" /> : <Maximize2 className="icon" />}
              </button>
            </div>
          </div>
          <textarea
            className="html-input"
            value={html}
            onChange={(e) => setHtml(e.target.value)}
            placeholder="여기에 HTML 코드를 작성하세요..."
            spellCheck={false}
          />
        </div>
        )}

        {(activeTab === 'preview' || activeTab === 'both') && (
          <div 
            className={`preview-pane ${isPreviewFullscreen ? 'fullscreen' : ''} ${draggingPane === 'preview' ? 'dragging-pane' : ''}`}
            style={{
              position: previewPosition.x !== 0 || previewPosition.y !== 0 ? 'absolute' : 'static',
              left: previewPosition.x !== 0 ? `${previewPosition.x}px` : 'auto',
              top: previewPosition.y !== 0 ? `${previewPosition.y}px` : 'auto',
              width: previewPosition.x !== 0 || previewPosition.y !== 0 ? 'calc(50% - 10px)' : 'auto',
              height: previewPosition.x !== 0 || previewPosition.y !== 0 ? '600px' : 'auto',
              zIndex: (draggingPane === 'preview' || frontPane === 'preview') ? 1000 : 1
            }}
            onClick={() => setFrontPane('preview')}
          >
            <div 
              className="pane-header draggable-header"
              onMouseDown={(e) => handlePaneDragStart('preview', e)}
              style={{ cursor: 'move' }}
            >
              <h3>👁️ 미리보기</h3>
              <div className="header-right">
                <button 
                  className="popup-btn" 
                  onClick={openPreviewInNewWindow}
                  title="새 창으로 열기"
                >
                  <Eye className="icon" />
                </button>
                <button 
                  className="preview-copy-btn"
                  onClick={handleCopy}
                  title="내용 복사"
                >
                  {copied ? <Check className="icon" /> : <Copy className="icon" />}
                </button>
                <button 
                  className="tts-btn" 
                  onClick={handleTextToSpeech}
                  title={isSpeaking ? "읽기 중지" : "읽어주기"}
                >
                  {isSpeaking ? <VolumeX className="icon" /> : <Volume2 className="icon" />}
                </button>
                <button 
                  onClick={runCode}
                  className="refresh-preview-btn"
                  title="미리보기 새로고침"
                >
                  <RotateCcw className="icon" />
                </button>
                <button 
                  className="fullscreen-btn" 
                  onClick={() => setIsPreviewFullscreen(!isPreviewFullscreen)}
                  title={isPreviewFullscreen ? "축소" : "전체화면"}
                >
                  {isPreviewFullscreen ? <Minimize2 className="icon" /> : <Maximize2 className="icon" />}
                </button>
              </div>
            </div>
            <iframe
                // ref={iframeRef}  // ref 제거
                className="html-preview"
                title="HTML Preview"
                // html 내용을 srcdoc 속성으로 전달
                srcDoc={html} 
                sandbox="allow-scripts allow-same-origin allow-modals"
            />
          </div>
        )}
      </div>
      
      {/* 드래그 중일 때 표시할 오버레이 */}
      {isDragging && (
        <div className="drag-overlay">
          <p>여기에 HTML 파일을 드롭하세요</p>
        </div>
      )}

      <div className="html-editor-footer">
        <div className="syntax-help">
          <strong>HTML 편집기:</strong>
          <span>HTML + CSS + JavaScript 지원</span>
          <span>실시간 미리보기</span>
          <span>파일 업로드/다운로드</span>
        </div>
      </div>
    </div>
  );
};

export default HTMLEditor;